/**
 * Widget-Editor — Modell des Kachel-Ablaufs und der Folgefragen (Reisegruppen-Widget T5,
 * docs/PLAN-REISEGRUPPEN-WIDGET.md A2/A4, Punkt 129).
 *
 * Reine Funktionen und Konstanten, kein React. Der Editor fuehrt den Ablauf als Formular-Zeile
 * (`AblaufForm`, mit Rohtext fuer die Gruppengroessen), gespeichert wird die Normalform aus
 * `apps/api/src/lib/widget-formular.ts` (`AblaufKonfig`). Der Server prueft alles erneut; hier
 * wird nur VOR dem Speichern gemeldet, was der Betreiber sofort selbst beheben kann.
 *
 * Die Konstanten unten sind Spiegel des Server-Modells. Eine Ratsche in
 * `apps/api/test/widget-formular.test.ts` liest DIESE Datei als Text und haelt sie deckungsgleich
 * (Sprachen, Grenzen, Dankeseiten-Fragen) — eine Frage, die nur der Server kennt, waere im Editor
 * unerreichbar; eine, die nur der Editor kennt, brächte beim Speichern ein 422.
 *
 * Beträge kommen hier nirgends vor: Menüs werden nur mit einem Katalog-Paket VERKNÜPFT, der Preis
 * steht im Katalog und wird serverseitig gelesen (Preis-Integrität).
 */
import { bereichText, findeKreis, type Bedingungen } from "@maestro/widgets/regeln";

export const ABLAUF_SPRACHEN = ["de", "en", "it", "fr"] as const;
export type AblaufSprache = (typeof ABLAUF_SPRACHEN)[number];
export const ABLAUF_SPRACHE_LABEL: Record<AblaufSprache, string> = {
  de: "Deutsch",
  en: "English",
  it: "Italiano",
  fr: "Français",
};

export const MAX_GAESTE_BEREICHE = 8;
export const MAX_MENUES = 6;
export const MAX_BLICK_ZEILEN = 4;
export const MAX_BLICK_LAENGE = 120;
export const MAX_HINWEIS_UEBER_LAENGE = 200;
export const MAX_URL_LAENGE = 300;
export const MAX_TELEFON_LAENGE = 40;
export const MAX_TELEFON_SPRACHEN_LAENGE = 80;
export const MAX_ANTWORTZUSAGE_LAENGE = 120;
export const MAX_FRIST_TAGE = 60;
export const MAX_BEDINGUNGEN = 12;
export const MAX_BEDINGUNG_WERTE = 20;

/**
 * Fragen, die der Gast NACH dem Absenden auf der Dankeseite nachreichen kann — Schluessel und
 * Beschriftung wortgleich mit `NACHFRAGE_FELDER` im Server (Ratsche). `optionen` nur bei Auswahl.
 */
export const EDITOR_NACHFRAGE_FELDER: { key: string; label: string; typ: string; optionen?: string[] }[] = [
  { key: "mahlzeit", label: "Mittag- oder Abendessen?", typ: "select", optionen: ["mittag", "abend", "offen"] },
  { key: "ankunft", label: "Ankunft (ungefähr)", typ: "time" },
  { key: "mobil", label: "Handynummer für den Reisetag", typ: "tel" },
  { key: "firma", label: "Firma / Organisation", typ: "text" },
  { key: "rolle", label: "Sie fragen an als", typ: "select", optionen: ["reiseveranstalter", "reiseleitung", "firma", "privat"] },
  { key: "ernaehrung", label: "Besondere Ernährung?", typ: "select", optionen: ["ja", "nein"] },
  { key: "ernaehrungDetails", label: "Ernährung: was sollen wir einplanen? Bitte keine Namen.", typ: "textarea" },
  { key: "reiseplan", label: "Reiseplan (PDF, bitte ohne Passdaten)", typ: "file" },
  { key: "nachricht", label: "Nachricht", typ: "textarea" },
];

/** Anzeigetext eines Auswahlwertes der Dankeseite (der gespeicherte Wert bleibt der Schluessel). */
export const NACHFRAGE_WERT_LABEL: Record<string, string> = {
  mittag: "Mittagessen",
  abend: "Abendessen",
  offen: "noch offen",
  reiseveranstalter: "Reiseveranstalter",
  reiseleitung: "Reiseleitung",
  firma: "Firma",
  privat: "Privat",
  ja: "Ja",
  nein: "Nein",
};

/** Vorgabe der Dankeseite (Plan A2, Beispielkonfiguration). */
export const STANDARD_DANKESEITE = ["mahlzeit", "ankunft", "mobil", "firma", "rolle", "ernaehrung", "ernaehrungDetails", "reiseplan", "nachricht"];

/**
 * Vorschlag fuer das Antwortversprechen je Sprache. Bewusst KEINE Stundenzahl: kuerzer versprechen darf ein
 * Betrieb erst, wenn seine gemessene Antwortzeit es traegt (Antoine, 30.09.2026).
 */
export const ANTWORTZUSAGE_VORSCHLAG: Record<AblaufSprache, string> = {
  de: "Wir antworten in der Regel innerhalb eines Werktags.",
  en: "We usually reply within one working day.",
  it: "Di norma rispondiamo entro un giorno lavorativo.",
  fr: "Nous répondons généralement sous un jour ouvrable.",
};

/** Vorgabe der Gruppengroessen (Untergrenzen; die letzte, 101, heisst „über 100“). */
export const STANDARD_GAESTE_BEREICHE = [10, 25, 40, 60, 80, 101];

export interface MenueZeile {
  schluessel: string;
  label: string;
  paketId: string | null;
}

/** Der Kachel-Ablauf als Editor-Zeile. Alles Text, was der Betreiber tippt; geparst wird beim Speichern. */
export interface AblaufForm {
  /** Vorlage des Ablaufs; der Editor reicht sie unveraendert durch (die Vorlage `anlass` wird nicht im Editor gewaehlt). */
  vorlage: "gruppe" | "anlass";
  /** Fester Termin oder Monatsauswahl der Vorlage `anlass`; unveraendert durchgereicht, `undefined` = nicht gesetzt. */
  termin?: Record<string, unknown>;
  /** Kalender-Einstellung: Monat, in dem der Kalender oeffnet (1-12); `undefined` = je nach Anlass. */
  kalender?: { monat?: number };
  /** Herkunftskennung der Anfragen (`sourceDetail`); unveraendert durchgereicht. */
  eingang?: string;
  sprache: AblaufSprache;
  /** Untergrenzen, kommagetrennt („10, 25, 40“). */
  gaesteText: string;
  monate: number;
  spaeter: boolean;
  offen: boolean;
  eventType: string;
  serviceKind: "event" | "catering";
  menues: MenueZeile[];
  dankeseite: string[];
  blick: string[];
  hinweisUeber: string;
  kleineGruppeUrl: string;
  /** Anzeigetext der Telefonnummer, z. B. „+49 89 51519696“ (der tel:-Link entsteht daraus). */
  telefon: string;
  /** Sprachen am Telefon, z. B. „Deutsch, Italiano, English“. */
  telefonSprachen: string;
  /** Frist in Tagen fuer „Finale Personenzahl … bis {frist} vorher“; leer = der Satz entfaellt. */
  fristTage: string;
  menueUrl: string;
  anfahrtUrl: string;
  /** Das Antwortversprechen des Betriebs in eigenen Worten; leer = gar keins (die gemessene Zeit erscheint dann, wenn sie es traegt). */
  antwortzusage: string;
}

export function standardAblauf(): AblaufForm {
  return {
    vorlage: "gruppe",
    sprache: "de",
    gaesteText: STANDARD_GAESTE_BEREICHE.join(", "),
    monate: 6,
    spaeter: true,
    offen: true,
    eventType: "Reisegruppe",
    serviceKind: "event",
    menues: [],
    dankeseite: [...STANDARD_DANKESEITE],
    blick: [],
    hinweisUeber: "",
    kleineGruppeUrl: "",
    telefon: "",
    telefonSprachen: "",
    fristTage: "",
    menueUrl: "",
    anfahrtUrl: "",
    antwortzusage: ANTWORTZUSAGE_VORSCHLAG.de,
  };
}

function alsObjekt(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function texte(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

/** `draftConfig.formular.ablauf` in eine Editor-Zeile uebersetzen (defensiv — fremdes JSON). `null` = kein Ablauf. */
export function leseAblauf(config: Record<string, unknown>): AblaufForm | null {
  const formular = alsObjekt(config.formular);
  const a = formular ? alsObjekt(formular.ablauf) : null;
  if (!a) return null;
  const vorgabe = standardAblauf();
  const fest = alsObjekt(a.festeWerte) ?? {};
  const bereiche = Array.isArray(a.gaesteBereiche) ? a.gaesteBereiche.filter((n): n is number => typeof n === "number") : [];
  const termin = alsObjekt(a.termin);
  const kal = alsObjekt(a.kalender);
  const kalMonat = kal && typeof kal.monat === "number" && kal.monat >= 1 && kal.monat <= 12 ? kal.monat : undefined;
  return {
    ...(kal ? { kalender: kalMonat ? { monat: kalMonat } : {} } : {}),
    vorlage: a.vorlage === "anlass" ? "anlass" : "gruppe",
    ...(termin ? { termin } : {}),
    ...(typeof a.eingang === "string" ? { eingang: a.eingang } : {}),
    sprache: (ABLAUF_SPRACHEN as readonly string[]).includes(a.sprache as string) ? (a.sprache as AblaufSprache) : vorgabe.sprache,
    gaesteText: bereiche.length > 0 ? bereiche.join(", ") : vorgabe.gaesteText,
    monate: typeof a.monate === "number" ? a.monate : vorgabe.monate,
    spaeter: a.spaeter !== false,
    offen: a.offen !== false,
    eventType: typeof fest.eventType === "string" ? fest.eventType : "",
    serviceKind: fest.serviceKind === "catering" ? "catering" : "event",
    menues: (Array.isArray(a.menues) ? a.menues : [])
      .map((m) => alsObjekt(m))
      .filter((m): m is Record<string, unknown> => m !== null)
      .map((m) => ({
        schluessel: typeof m.schluessel === "string" ? m.schluessel : "",
        label: typeof m.label === "string" ? m.label : "",
        paketId: typeof m.paketId === "string" ? m.paketId : null,
      })),
    dankeseite: texte(a.dankeseite),
    blick: texte(a.blick),
    hinweisUeber: typeof a.hinweisUeber === "string" ? a.hinweisUeber : "",
    kleineGruppeUrl: typeof a.kleineGruppeUrl === "string" ? a.kleineGruppeUrl : "",
    telefon: typeof a.telefon === "string" ? a.telefon : "",
    telefonSprachen: typeof a.telefonSprachen === "string" ? a.telefonSprachen : "",
    fristTage: typeof a.fristTage === "number" ? String(a.fristTage) : "",
    menueUrl: typeof a.menueUrl === "string" ? a.menueUrl : "",
    anfahrtUrl: typeof a.anfahrtUrl === "string" ? a.anfahrtUrl : "",
    // Eine bestehende Konfiguration ohne Versprechen bleibt ohne: nichts wird stillschweigend hinzugefuegt.
    antwortzusage: typeof a.antwortzusage === "string" ? a.antwortzusage : "",
  };
}

/** „10, 25 40" → [10, 25, 40]. Nicht lesbare Teile fallen weg (die Pruefung meldet dann zu wenige/unsortierte). */
export function parseGaesteBereiche(text: string): number[] {
  return text
    .split(/[\s,;]+/)
    .filter(Boolean)
    .map((t) => (/^\d+$/.test(t) ? Number(t) : NaN))
    .filter((n) => Number.isFinite(n));
}

/** Klartext-Meldungen zu den Gruppengroessen — leer, wenn alles passt. */
export function pruefeGaesteText(text: string): string[] {
  const roh = text.split(/[\s,;]+/).filter(Boolean);
  const zahlen = parseGaesteBereiche(text);
  const fehler: string[] = [];
  if (roh.length !== zahlen.length) fehler.push("Die Gruppengrößen dürfen nur ganze Zahlen enthalten, zum Beispiel 10, 25, 40.");
  if (zahlen.length < 2) fehler.push("Mindestens zwei Gruppengrößen (Untergrenzen) sind nötig.");
  if (zahlen.length > MAX_GAESTE_BEREICHE) fehler.push(`Höchstens ${MAX_GAESTE_BEREICHE} Gruppengrößen.`);
  if (zahlen.some((n, i) => i > 0 && n <= (zahlen[i - 1] as number))) {
    fehler.push("Die Untergrenzen der Gruppengrößen müssen streng aufsteigend sein, zum Beispiel 10, 25, 40.");
  }
  if (zahlen.some((n) => n < 1 || n > 100_000)) fehler.push("Die Gruppengrößen müssen zwischen 1 und 100000 liegen.");
  return fehler;
}

/** Anzeigetexte der Bereiche („10–24“, „über 100“) aus dem Rohtext; ungueltiger Text → leere Liste. */
export function bereichLabels(gaesteText: string): { untergrenze: number; text: string }[] {
  const zahlen = parseGaesteBereiche(gaesteText);
  if (pruefeGaesteText(gaesteText).length > 0) return [];
  return zahlen.map((u) => ({ untergrenze: u, text: bereichText(zahlen, u) ?? String(u) }));
}

export interface AblaufErgebnis {
  ablauf: Record<string, unknown>;
  fehler: string[];
}

/** Editor-Zeile → gespeicherte Form. `fehler` sind Meldungen, die der Server ohnehin mit 422 zurueckgaebe. */
export function baueAblauf(f: AblaufForm): AblaufErgebnis {
  const fehler = pruefeGaesteText(f.gaesteText);
  const menues = f.menues.filter((m) => m.label.trim());
  const blick = f.blick.map((z) => z.trim()).filter(Boolean);
  for (const [wert, was] of [
    [f.kleineGruppeUrl, "für kleine Gruppen"],
    [f.menueUrl, "zu den Menüs"],
    [f.anfahrtUrl, "zur Anfahrt"],
  ] as const) {
    if (wert.trim() && !/^https:\/\/[^\s<>"]+$/.test(wert.trim())) fehler.push(`Der Link ${was} muss mit https:// beginnen.`);
  }
  const telefon = f.telefon.trim();
  if (telefon && telefon.replace(/\D/g, "").length < 5) fehler.push("Die Telefonnummer braucht mindestens 5 Ziffern, zum Beispiel +49 89 51519696.");
  const frist = f.fristTage.trim();
  const fristZahl = /^\d+$/.test(frist) ? Number(frist) : NaN;
  if (frist && !(fristZahl >= 1 && fristZahl <= MAX_FRIST_TAGE)) fehler.push(`Die Frist ist eine ganze Zahl von Tagen zwischen 1 und ${MAX_FRIST_TAGE}.`);
  const festeWerte: Record<string, unknown> = { serviceKind: f.serviceKind };
  if (f.eventType.trim()) festeWerte.eventType = f.eventType.trim();
  return {
    fehler,
    ablauf: {
      art: "kacheln",
      vorlage: f.vorlage,
      ...(f.termin ? { termin: f.termin } : {}),
      ...(f.kalender ? { kalender: f.kalender } : {}),
      ...(f.eingang ? { eingang: f.eingang } : {}),
      sprache: f.sprache,
      gaesteBereiche: parseGaesteBereiche(f.gaesteText),
      monate: f.monate,
      spaeter: f.spaeter,
      offen: f.offen,
      festeWerte,
      menues: menues.map((m) => ({ schluessel: m.schluessel, label: m.label.trim(), paketId: m.paketId })),
      dankeseite: [...f.dankeseite],
      blick,
      ...(f.hinweisUeber.trim() ? { hinweisUeber: f.hinweisUeber.trim() } : {}),
      ...(f.kleineGruppeUrl.trim() ? { kleineGruppeUrl: f.kleineGruppeUrl.trim() } : {}),
      ...(telefon ? { telefon } : {}),
      ...(f.telefonSprachen.trim() ? { telefonSprachen: f.telefonSprachen.trim() } : {}),
      ...(Number.isFinite(fristZahl) && frist ? { fristTage: fristZahl } : {}),
      ...(f.menueUrl.trim() ? { menueUrl: f.menueUrl.trim() } : {}),
      ...(f.anfahrtUrl.trim() ? { anfahrtUrl: f.anfahrtUrl.trim() } : {}),
      ...(f.antwortzusage.trim() ? { antwortzusage: f.antwortzusage.trim() } : {}),
    },
  };
}

/** Naechster freier Menue-Schluessel („m1“, „m2“ …) — Server-Muster ^[a-z][a-z0-9_]{0,19}$. */
export function neuerMenueSchluessel(menues: readonly MenueZeile[]): string {
  const belegt = new Set(menues.map((m) => m.schluessel));
  let n = 1;
  while (belegt.has(`m${n}`)) n++;
  return `m${n}`;
}

/* ---------------------------------------------------------------------------------------------
 * Folgefragen (Punkt 129)
 * ------------------------------------------------------------------------------------------- */

export interface BedingungZeile {
  feld: string;
  werte: string[];
}
/** Zielfeld -> „nur zeigen, wenn Feld X einen dieser Werte hat“. */
export type BedingungenForm = Record<string, BedingungZeile>;

export function leseBedingungen(config: Record<string, unknown>): BedingungenForm {
  const formular = alsObjekt(config.formular);
  const roh = formular ? alsObjekt(formular.bedingungen) : null;
  const aus: BedingungenForm = {};
  if (!roh) return aus;
  for (const [ziel, b] of Object.entries(roh)) {
    const o = alsObjekt(b);
    if (!o || typeof o.feld !== "string") continue;
    aus[ziel] = { feld: o.feld, werte: texte(o.werte) };
  }
  return aus;
}

/** Nur vollstaendige Bedingungen (Quelle + mindestens ein Wert) werden gespeichert; leer → `null`. */
export function baueBedingungen(b: BedingungenForm): Record<string, { feld: string; werte: string[] }> | null {
  const aus: Record<string, { feld: string; werte: string[] }> = {};
  for (const [ziel, z] of Object.entries(b)) {
    if (z.feld && z.werte.length > 0) aus[ziel] = { feld: z.feld, werte: [...z.werte] };
  }
  return Object.keys(aus).length > 0 ? aus : null;
}

/** Was die Folgefragen-Auswahl vom Formular wissen muss (EditorForm erfuellt es). */
export interface FolgefrageKontext {
  standardfelder: readonly string[];
  freieFelder: readonly { key: string; typ: string; label: string; pflicht: boolean; optionenText: string }[];
  ablaufAn: boolean;
  ablauf: AblaufForm;
  bedingungen: BedingungenForm;
}

export interface FolgefrageWert {
  wert: string;
  label: string;
}

export interface FolgefrageFeld {
  key: string;
  label: string;
  /** Wo das Feld sitzt — nur zur Anzeige („Dankeseite“). */
  ort: string | null;
  /** Freies Feld mit Pflicht-Haken: eine Folgefrage daran ist eine Pflicht-Folgefrage. */
  pflicht: boolean;
  /** Antworten, die eine andere Frage ausloesen koennen; `null` = dieses Feld kann keine Folgefrage ausloesen. */
  antworten: FolgefrageWert[] | null;
}

const STANDARD_LABEL: Record<string, string> = {
  company: "Firma",
  serviceKind: "Wo findet die Veranstaltung statt?",
  deliveryAddress: "Lieferadresse",
  budget: "Budgetvorstellung (Freitext)",
  dietaryRequirements: "Unverträglichkeiten / Ernährung",
  dateFlexible: "Termin ist flexibel",
  arrivalTime: "Gewünschte Uhrzeit",
  preferredMenu: "Menü-Wunsch",
  format: "Format der Veranstaltung",
};

/**
 * Alle Felder, an denen eine Folgefrage haengen darf (Ziel) — in Formular-Reihenfolge: Ablauf-Fragen,
 * Standardfelder, eigene Felder, Dankeseite. Jedes traegt, wenn es als AUSLOESER taugt, seine Antworten.
 * Ausloeser sind Auswahl- und Ankreuzfelder sowie die Fragen des Ablaufs (Server: `quellWerte`).
 */
export function folgefrageFelder(k: FolgefrageKontext): FolgefrageFeld[] {
  const felder: FolgefrageFeld[] = [];
  if (k.ablaufAn) {
    const bereiche = bereichLabels(k.ablauf.gaesteText);
    felder.push({
      key: "gaesteBereich",
      label: "Gruppengröße",
      ort: null,
      pflicht: false,
      antworten: bereiche.map((b) => ({ wert: String(b.untergrenze), label: b.text })),
    });
    felder.push({ key: "gaesteGenau", label: "Genaue Personenzahl (optional)", ort: null, pflicht: false, antworten: null });
    felder.push({
      key: "zeitraum",
      label: "Wann ungefähr?",
      ort: null,
      pflicht: false,
      antworten: k.ablauf.offen ? [{ wert: "offen", label: "Noch offen" }] : [],
    });
  }
  for (const key of k.standardfelder) {
    if (!(key in STANDARD_LABEL)) continue;
    felder.push({
      key,
      label: STANDARD_LABEL[key] as string,
      ort: null,
      pflicht: false,
      antworten: key === "dateFlexible" ? [{ wert: "true", label: "angekreuzt" }] : null,
    });
  }
  for (const f of k.freieFelder) {
    if (!f.key.trim() || !f.label.trim()) continue;
    let antworten: FolgefrageWert[] | null = null;
    if (f.typ === "checkbox") antworten = [{ wert: "true", label: "angekreuzt" }];
    else if (f.typ === "select") {
      antworten = f.optionenText
        .split("\n")
        .map((o) => o.trim())
        .filter(Boolean)
        .map((o) => ({ wert: o, label: o }));
    }
    felder.push({ key: f.key, label: f.label, ort: null, pflicht: f.pflicht, antworten });
  }
  if (k.ablaufAn) {
    for (const key of k.ablauf.dankeseite) {
      const n = EDITOR_NACHFRAGE_FELDER.find((x) => x.key === key);
      if (!n) continue;
      felder.push({
        key,
        label: n.label,
        ort: "Dankeseite",
        pflicht: false,
        antworten: n.typ === "select" && n.optionen ? n.optionen.map((o) => ({ wert: o, label: NACHFRAGE_WERT_LABEL[o] ?? o })) : null,
      });
    }
  }
  return felder;
}

/** Ein Kreis in den Bedingungen mit lesbaren Namen („Feld A → Feld B → Feld A“), sonst `null`. */
export function kreisText(b: BedingungenForm, felder: readonly FolgefrageFeld[]): string | null {
  const kreis = findeKreis(b as Bedingungen);
  if (!kreis) return null;
  const name = (k: string) => felder.find((f) => f.key === k)?.label ?? k;
  return kreis.map(name).join(" → ");
}

/**
 * Bedingungen, die noch zum Formular passen: Ziel und Ausloeser existieren, der Ausloeser kann
 * eine Folgefrage ausloesen, und die Werte sind noch echte Antworten. Der Rest faellt weg.
 */
export function bereinigeBedingungen(b: BedingungenForm, felder: readonly FolgefrageFeld[]): BedingungenForm {
  const aus: BedingungenForm = {};
  for (const [ziel, z] of Object.entries(b)) {
    if (!felder.some((f) => f.key === ziel)) continue;
    const quelle = felder.find((f) => f.key === z.feld);
    if (!quelle || !quelle.antworten) continue;
    const erlaubt = new Set(quelle.antworten.map((a) => a.wert));
    const werte = z.werte.filter((w) => erlaubt.has(w));
    if (werte.length > 0) aus[ziel] = { feld: z.feld, werte };
  }
  return aus;
}
